import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Недописанные сообщения по переписке.
 *
 * Композер комнаты сессии и композер треда – один и тот же `ChatInput`, и человек ходит между
 * ними на полуслове: свернул окно в пилюлю, ушёл в тред, обновил страницу. Пока текст лежал в
 * локальном состоянии, каждый такой переход стирал набранное – поэтому он переехал сюда.
 */

/**
 * Предел сообщения чата (`CHAT_MESSAGE_MAX_LENGTH`). Держим числом, а не импортом из хука
 * композера: стор обязан оставаться свободным от React, иначе его не подключить из обработчика
 * сокета и не проверить тестом в node-окружении. Длиннее хранить незачем – отправить всё равно
 * нельзя.
 */
export const CHAT_DRAFT_MAX_LENGTH = 500;

/**
 * Сколько переписок помним. Persist пишет весь объект целиком на каждое нажатие клавиши, и
 * несдержанная карта черновиков растёт вместе с числом чатов за всю жизнь вкладки. Двадцати
 * хватает на любой реальный день, а вытесняем самую давнюю.
 */
export const CHAT_DRAFT_MAX_CHATS = 20;

/** Ключ записи в хранилище вкладки. Его же снимает выход из аккаунта. */
export const CHAT_DRAFTS_STORAGE_KEY = 'chat-drafts';

const memoryStorage = (): Storage => {
  const data = new Map<string, string>();

  return {
    get length() {
      return data.size;
    },
    clear: () => data.clear(),
    getItem: (key: string) => data.get(key) ?? null,
    key: (index: number) => [...data.keys()][index] ?? null,
    removeItem: (key: string) => void data.delete(key),
    setItem: (key: string, value: string) => void data.set(key, value),
  };
};

let fallbackStorage: Storage | null = null;

/**
 * Хранилище, переживающее запрет данных сайта и сервер.
 *
 * Обращение к `sessionStorage` бросает `SecurityError`, когда браузеру запрещено хранить данные
 * сайта, а на сервере глобали нет вовсе. `createJSONStorage` в таком случае возвращает
 * `undefined`, и zustand не создаёт `api.persist` – дальше любой вызов `persist.*` роняет экран.
 * Подставляем хранилище в памяти: черновик живёт до перезагрузки, зато переписка работает.
 *
 * Именно `sessionStorage`, а не `localStorage`: черновик принадлежит этой вкладке. На общем
 * хранилище две открытые вкладки затирали бы черновики друг другу (persist пишет карту целиком,
 * чужих правок не видя), а после выхода из аккаунта набранное осталось бы лежать на чужом
 * компьютере.
 */
const safeSessionStorage = (): Storage => {
  try {
    // Обращение к глобали, а не к `window.sessionStorage`: на сервере окна нет. Одного чтения
    // мало – часть браузеров пускает к объекту и падает на записи.
    const probe = '__tp_probe__';
    sessionStorage.setItem(probe, probe);
    sessionStorage.removeItem(probe);
    return sessionStorage;
  } catch {
    fallbackStorage ??= memoryStorage();
    return fallbackStorage;
  }
};

type ChatDraftSlice = {
  /** chatId → недописанный текст. Пустых строк в карте не бывает. */
  drafts: Record<string, string>;
  /**
   * Те же chatId в порядке последней правки, свежий – в конце. Отдельным списком, а не порядком
   * ключей карты: у числовых ключей порядок в объекте свой, и вытеснение выбрасывало бы не то.
   */
  recentChatIds: string[];
  getDraft: (chatId: string | null | undefined) => string;
  setDraft: (chatId: string | null | undefined, text: string) => void;
  clearDraft: (chatId: string | null | undefined) => void;
};

/**
 * Обрезать черновик, не разрубая символ пополам.
 *
 * `slice` считает единицы UTF-16, а эмодзи – суррогатная пара, две единицы. Ровно на границе
 * лимита пара распадалась: в хранилище оставалась половинка, а на экране «□» вместо смайла.
 * Идём по кодовым точкам и берём их целиком, пока помещаются.
 *
 * Бюджет при этом остаётся в единицах UTF-16 – тот же, которым меряет отправку композер
 * (`preparedText.length > CHAT_MESSAGE_MAX_LENGTH`). Считать бюджет в кодовых точках значило бы
 * хранить черновик, который заведомо не отправить.
 */
const truncateToLimit = (text: string, limit: number): string => {
  if (text.length <= limit) {
    return text;
  }

  let result = '';

  // Строковый `for..of` идёт по кодовым точкам, а не по единицам UTF-16.
  for (const char of text) {
    if (result.length + char.length > limit) {
      break;
    }

    result += char;
  }

  return result;
};

const withoutDraft = (state: ChatDraftSlice, chatId: string): Partial<ChatDraftSlice> | null => {
  if (state.drafts[chatId] === undefined) {
    return null;
  }

  const drafts = { ...state.drafts };
  delete drafts[chatId];

  return { drafts, recentChatIds: state.recentChatIds.filter((id) => id !== chatId) };
};

export const useChatDraftStore = create<ChatDraftSlice>()(
  persist(
    (set, get) => ({
      drafts: {},
      recentChatIds: [],
      getDraft: (chatId) => (chatId ? (get().drafts[chatId] ?? '') : ''),
      setDraft: (chatId, text) => {
        if (!chatId) {
          return;
        }

        const next = truncateToLimit(text, CHAT_DRAFT_MAX_LENGTH);

        set((state) => {
          if (next.length === 0) {
            return withoutDraft(state, chatId) ?? state;
          }

          // Тот же текст – тот же объект: иначе каждый кадр композера перерисовывал бы всех,
          // кто читает карту, и заново писал бы хранилище.
          if (state.drafts[chatId] === next) {
            return state;
          }

          const drafts = { ...state.drafts, [chatId]: next };
          const recentChatIds = [...state.recentChatIds.filter((id) => id !== chatId), chatId];

          while (recentChatIds.length > CHAT_DRAFT_MAX_CHATS) {
            const evicted = recentChatIds.shift();
            if (evicted !== undefined) {
              delete drafts[evicted];
            }
          }

          return { drafts, recentChatIds };
        });
      },
      clearDraft: (chatId) => {
        if (!chatId) {
          return;
        }

        set((state) => withoutDraft(state, chatId) ?? state);
      },
    }),
    {
      name: CHAT_DRAFTS_STORAGE_KEY,
      storage: createJSONStorage(() => safeSessionStorage()),
    }
  )
);

/**
 * Выбросить все черновики. Нужен выходу из аккаунта: набранное в переписках – такая же личная
 * переписка, как и сами сообщения, и на общем компьютере она не должна достаться следующему,
 * кто войдёт в этой вкладке. Сосед по выходу – `clearPostFormDraft`.
 *
 * Сперва состояние, потом хранилище: `setState` у persist сам пишет карту в хранилище, и в
 * обратном порядке после очистки там бы снова появилась (пустая, но всё же) запись.
 */
export const clearChatDrafts = () => {
  useChatDraftStore.setState({ drafts: {}, recentChatIds: [] });

  try {
    useChatDraftStore.persist.clearStorage();
  } catch {
    // Хранилище закрыто – чистить нечего.
  }
};
